import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Images, ArrowLeft, Star, Users } from "lucide-react";
import { PROPERTIES } from "../lib/properties";
import PhotoGallery from "../components/PhotoGallery";

export default function NestLanding() {
  const { nestId } = useParams();
  const nest = PROPERTIES.find((p) => p.id === nestId);

  if (!nest) {
    return <Navigate to="/" replace />;
  }

  return (
    <div>
      <section className="relative min-h-[calc(100vh-64px)] flex items-center overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${nest.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/70 to-[#FFFFFF]" />

        <div className="relative w-full max-w-3xl mx-auto px-5 sm:px-6 py-14 sm:py-20 text-center">
          <Link
            to="/"
            data-testid="nest-back-home"
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-[#717171] hover:text-[#111]"
          >
            <ArrowLeft size={14} /> All nests
          </Link>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-6 text-[11px] sm:text-xs uppercase tracking-[0.25em] text-[#111111] font-semibold"
          >
            Welcome to {nest.name}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="font-heading text-[36px] leading-[1.05] sm:text-6xl font-extrabold text-[#111] tracking-tighter mt-4"
          >
            Leave a memory
            <br />
            <span className="text-[#111111]">from your stay.</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mt-6 inline-flex items-center gap-3 text-sm text-[#333] bg-white/80 backdrop-blur-md rounded-full px-4 py-1.5 border border-white/60 shadow-sm"
          >
            <span className="inline-flex items-center gap-1">
              <Star size={14} className="text-[#111]" fill="#222" />
              {nest.rating.toFixed(2)}
              <span className="text-[#717171]">· {nest.reviews}</span>
            </span>
            <span className="text-[#EBEBEB]">|</span>
            <span className="inline-flex items-center gap-1">
              <Users size={14} /> {nest.guests}
            </span>
            <span className="text-[#EBEBEB]">|</span>
            <span className="text-[#717171]">{nest.location}</span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="mt-5 sm:mt-6 text-base sm:text-lg text-[#333] max-w-xl mx-auto leading-relaxed"
          >
            Snap a photo of a favorite corner of {nest.name}, drop a sticky
            note, and take home a printable polaroid keepsake.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-9 sm:mt-11 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
          >
            <Link
              to={`/sign?camera=1&nest=${nest.id}`}
              data-testid="nest-camera-cta"
              className="gb-btn-primary inline-flex items-center gap-2 text-base sm:text-lg px-8 sm:px-10 py-4 w-full sm:w-auto justify-center"
            >
              <Camera size={20} />
              Take a picture
            </Link>
            <Link
              to={`/gallery?nest=${nest.id}`}
              data-testid="nest-gallery-cta"
              className="gb-btn-ghost inline-flex items-center gap-2 w-full sm:w-auto justify-center"
            >
              <Images size={16} />
              See {nest.name} pages
            </Link>
          </motion.div>

          <p className="mt-6 text-xs sm:text-sm text-[#717171]">
            <Link
              to={`/sign?nest=${nest.id}`}
              data-testid="nest-upload-link"
              className="underline underline-offset-2 text-[#111] hover:text-[#111111]"
            >
              Or upload a photo instead
            </Link>
          </p>
        </div>
      </section>

      {/* Photo gallery */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20 sm:pb-24 -mt-4 sm:mt-2">
        <div className="text-center mb-6 sm:mb-8">
          <p className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-[#111] font-semibold">
            Inside {nest.name}
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#111] mt-2 tracking-tighter">
            Every corner, in one place.
          </h2>
          <p className="text-sm text-[#5b5b5b] mt-2">
            Tap any photo to open the lightbox.
          </p>
        </div>
        <PhotoGallery photos={nest.photos} testIdPrefix={`nest-${nest.id}-photo`} />
      </section>
    </div>
  );
}
